import React, { useState } from 'react';
import { Calendar, Download, Filter, Share2 } from 'lucide-react';
import { AnalyticsSummary } from '../components/analytics/AnalyticsSummary';
import { BidPerformanceChart } from '../components/analytics/BidPerformanceChart';
import { ContractValueChart } from '../components/analytics/ContractValueChart';
import { AgencyDistribution } from '../components/analytics/AgencyDistribution';
import { ComplianceMetrics } from '../components/analytics/ComplianceMetrics';
import { PerformanceRadar } from '../components/analytics/PerformanceRadar';
import { WinRateHeatmap } from '../components/analytics/WinRateHeatmap';
import { RevenueProjection } from '../components/analytics/RevenueProjection';
import { CompetitorAnalysis } from '../components/analytics/CompetitorAnalysis';
import { ExportModal } from '../components/analytics/ExportModal';
import { ShareModal } from '../components/analytics/ShareModal';
import { FilterPanel } from '../components/analytics/FilterPanel';
import { useAnalyticsStore } from '../stores/analyticsStore';
import { toast } from 'react-hot-toast';

export function Analytics() {
  const [dateRange, setDateRange] = useState('30');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const { loading, error, fetchAnalytics, exportData } = useAnalyticsStore();

  const handleExport = async (format: 'pdf' | 'csv' | 'excel') => {
    try {
      await exportData(format);
      toast.success(`Analytics exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export analytics');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-8">
        <div className="absolute inset-0 bg-grid-white/[0.1] bg-[size:16px_16px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Analytics Dashboard</h1>
              <p className="mt-1 text-purple-100">
                Comprehensive insights into your contracting performance
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFilters(true)}
                className="inline-flex items-center rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <Filter className="mr-2 h-4 w-4" />
                Filters
              </button>
              <button
                onClick={() => setShowExportModal(true)}
                className="inline-flex items-center rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <Download className="mr-2 h-4 w-4" />
                Export
              </button>
              <button
                onClick={() => setShowShareModal(true)}
                className="inline-flex items-center rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <Share2 className="mr-2 h-4 w-4" />
                Share
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center">
            <Calendar className="mr-2 h-5 w-5 text-purple-300" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="rounded-lg border-0 bg-white/10 px-4 py-2 text-white placeholder-purple-200 backdrop-blur-sm transition-colors focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 12 months</option>
              <option value="ytd">Year to date</option>
            </select>
          </div>
        </div>
      </div>

      {/* Analytics Summary */}
      <div className="mt-4">
        <AnalyticsSummary />
      </div>

      {/* Main Content Grid */}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <BidPerformanceChart />
          <AgencyDistribution />
          <PerformanceRadar />
        </div>
        <div className="space-y-4">
          <ContractValueChart />
          <ComplianceMetrics />
          <WinRateHeatmap />
        </div>
      </div>

      {/* Full Width Charts */}
      <div className="mt-4 space-y-4">
        <RevenueProjection />
        <CompetitorAnalysis />
      </div>

      {/* Modals */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
      />
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
      <FilterPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
      />
    </div>
  );
}